import React, { useState } from 'react';
import styled from 'styled-components';
import { useI18next } from 'gatsby-plugin-react-i18next';
import { isMultilingual } from '../../../languages';
import withOrderContext from '../../withOrderContext';
import { Link } from '../../services/link';
import dropdownArrow from '../../assets/arrow-dropdown-list-light.svg';

interface AProps {
  isSelected: boolean;
}

type ButtonProps = {
  scrolled: boolean;
};

const PopupContainer = styled.div`
    position: relative;
`;

const Button = styled.button<ButtonProps>`
  background-color: ${(props) => (props.scrolled ? 'white' : 'transparent')};
  transition: background-color 0.3s ease, color 0.3s ease;
  border: none;
  padding: 10px;
  cursor: pointer;
  height: 100%;
  display: flex;
  align-items: center;
  gap: 5px;
`;

const Popup = styled.div`
    position: absolute;
    top: 100%;
    right: 0;
    background: white;
    border: 1px solid #ccc;
    padding: 10px;
`;

const LanguageItem = styled.div`
    display: flex;
    align-items: center;
    gap: 8px;
    margin-bottom: 5px;
`;

const Flag = styled.span`
    font-size: 16px;
`;

type ChevronProps = {
  rotate: number;
};
const Chevron = styled.img<ChevronProps>`
  width: 8px;
  ${(props) => props.rotate && 'transform: rotate(180deg)'};
`;

const languageFlags: { [key: string]: string } = {
  fr: '🇫🇷',
  en: '🇬🇧',
  es: '🇪🇸',
  it: '🇮🇹',
  de: '🇩🇪',
  pt: '🇵🇹',
};

const StyledText = styled.span<AProps>`
    text-decoration: ${({ isSelected }) => isSelected ? 'underline' : 'none'};
    font-weight: ${({ isSelected }) => isSelected ? '700' : '400'};
    color: black;
    font-size: 12px;
    letter-spacing: 0.5px;
    line-height: 24px;
    margin: 0px 5px;
`;

const A = styled(StyledText).attrs({ as: 'a' })``;
const StyledLink = styled(StyledText).attrs({ as: 'span' })``;

function getUrls(): { [key: string]: string } {
  if (process.env.GATSBY_ENV === 'PROD') {
    return {
      fr: 'https://tilli.fr',
      en: 'https://tilli.com',
      it: 'https://tilli.com/it',
      de: 'https://tilli.com/de',
      es: 'https://tilli.com/es',
      pt: 'https://tilli.com/pt',
    };
  }
  if (process.env.GATSBY_ENV === 'STAGING') {
    return {
      fr: 'https://staging.tilli.fr',
      en: 'https://staging.tilli.com',
      it: 'https://staging.tilli.com/it',
      de: 'https://staging.tilli.com/de',
      es: 'https://staging.tilli.com/es',
      pt: 'https://staging.tilli.com/pt',
    };
  }
  return {
    fr: 'http://localhost:3003',
    en: 'http://localhost:3004',
    it: 'http://localhost:3004/it',
    de: 'http://localhost:3004/de',
    es: 'http://localhost:3004/es',
    pt: 'http://localhost:3004/pt',
  };
}
interface LanguagesSwitcherProps {
  orderContext: {
    setSelectedLanguage: (_lng: string) => void;
  };
}
// eslint-disable-next-line max-lines-per-function
const LanguagesSwitcher: React.FC<LanguagesSwitcherProps> = ({ orderContext: { setSelectedLanguage } }) => {
  const [isOpen, setIsOpen] = useState(false);
  const { language, originalPath } = useI18next();
  const urls = getUrls();

  const languages = ['en', 'it', 'de', 'es', 'pt'];
  return (
    <PopupContainer>
      <Button scrolled={false} onClick={() => setIsOpen(!isOpen)}>
        <Flag>{languageFlags[language] || ''}</Flag>
        {' '}
        {language.toLocaleUpperCase()}
        <Chevron
          src={dropdownArrow}
          rotate={isOpen ? 1 : 0}
          alt="menu déroulant"
        />
      </Button>
      {isOpen && (
        <Popup>
          <LanguageItem>
            <Flag>{languageFlags.fr}</Flag>
            <A as="a" href={urls.fr + originalPath} isSelected={language === 'fr'}>FR</A>
          </LanguageItem>
          {languages.map((lng) => (
            <LanguageItem key={lng}>
              <Flag>{languageFlags[lng] || ''}</Flag>
              <Link
                to={isMultilingual ? originalPath : urls[lng]}
                locale={lng}
                style={{ textDecoration: 'none', color: 'black' }}
                placeholder=""
                onPointerEnterCapture={undefined}
                onPointerLeaveCapture={undefined}
              >
                <StyledLink
                  isSelected={language.toLocaleUpperCase() === lng.toLocaleUpperCase()}
                  onClick={() => {
                    setIsOpen(false);
                    setSelectedLanguage(lng);
                  }}
                >
                  {lng.toLocaleUpperCase()}
                </StyledLink>
              </Link>
            </LanguageItem>
          ))}
        </Popup>
      )}
    </PopupContainer>
  );
};

export default withOrderContext(LanguagesSwitcher);
